/**
 * Image optimization utilities for faster loading
 */

/**
 * Generate responsive image URLs with different sizes
 * @param {string} baseUrl - Base image URL
 * @param {string} size - Size variant (sm, md, lg, xl)
 * @returns {string} Optimized image URL
 */
export const getResponsiveImageUrl = (baseUrl, size = 'md') => {
  if (!baseUrl) return '';

  // Already an image file — never invent a different extension (e.g. .jpg -> .webp)
  if (/\.(webp|jpe?g|png|gif|svg)(\?.*)?$/i.test(baseUrl)) {
    // Under /optimized/, optional sized variant when requested
    if (baseUrl.includes('/optimized/') && size && size !== 'md') {
      return baseUrl.replace(
        /(\.(webp|jpe?g|png))(\?.*)?$/i,
        `-${size}$1$3`
      );
    }
    return baseUrl;
  }

  return baseUrl;
};

/**
 * Preload critical images
 * @param {string[]} imageUrls - Array of image URLs to preload
 */
export const preloadImages = (imageUrls) => {
  imageUrls.forEach((url) => {
    if (url) {
      const link = document.createElement('link');
      link.rel = 'preload';
      link.as = 'image';
      link.href = url;
      if (/^https?:\/\//i.test(url) && typeof window !== 'undefined' && !url.includes(window.location.origin)) {
        link.crossOrigin = 'anonymous';
      }
      document.head.appendChild(link);
    }
  });
};

/**
 * Create a low-quality image placeholder (LQIP)
 * @param {string} imageUrl - Original image URL
 * @returns {string} Base64 encoded placeholder
 */
export const createImagePlaceholder = (imageUrl) => {
  // Return a simple gradient placeholder as SVG
  return 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjMwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZGVmcz48bGluZWFyR3JhZGllbnQgaWQ9ImciIHgxPSIwJSIgeTE9IjAlIiB4Mj0iMTAwJSIgeTI9IjEwMCUiPjxzdG9wIG9mZnNldD0iMCUiIHN0b3AtY29sb3I9IiMzMzMiLz48c3RvcCBvZmZzZXQ9IjEwMCUiIHN0b3AtY29sb3I9IiM2NjYiLz48L2xpbmVhckdyYWRpZW50PjwvZGVmcz48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSJ1cmwoI2cpIi8+PC9zdmc+';
};

/**
 * Check if image is already cached
 * @param {string} imageUrl - Image URL to check
 * @returns {Promise<boolean>} True if cached
 */
export const isImageCached = async (imageUrl) => {
  if ('caches' in window) {
    const cache = await caches.open('akiblackcar-image-cache-v1');
    const response = await cache.match(imageUrl);
    return !!response;
  }
  return false;
};

/**
 * Get optimal image size based on container width
 * @param {number} containerWidth - Container width in pixels
 * @returns {string} Size variant
 */
export const getOptimalImageSize = (containerWidth) => {
  if (containerWidth <= 400) return 'sm';
  if (containerWidth <= 800) return 'md';
  if (containerWidth <= 1200) return 'lg';
  return 'xl';
};

/**
 * Generate srcset for responsive images
 * @param {string} baseUrl - Base image URL
 * @returns {string} Srcset string
 */
export const generateSrcSet = (baseUrl) => {
  if (!baseUrl) return '';

  // Only emit sized variants for known /optimized/ assets
  if (!baseUrl.includes('/optimized/')) {
    return baseUrl;
  }

  const match = baseUrl.match(/^(.*?)(\.(webp|jpe?g|png))(\?.*)?$/i);
  if (!match) return baseUrl;

  const [, basePath, ext, , query = ''] = match;
  return `${basePath}-sm${ext}${query} 400w, ${basePath}-md${ext}${query} 800w, ${basePath}-lg${ext}${query} 1200w, ${basePath}-xl${ext}${query} 1600w`;
};
